import {useMutation, useQueryClient} from '@tanstack/react-query';
import type {
  CreateGuestByParticipantPayload,
  CreateGuestByParticipantResponse,
  FieldsErrorResponse,
} from '@guestapp/sdk';
import {QUERY_CACHE_KEYS} from 'utils/constants';
import {useChekinSDK} from 'context/ChekinSDK';
import {useReservation} from 'context/reservation';

type UseGuestMutation = {
  onSuccess?: (data: CreateGuestByParticipantResponse) => void;
  onError?: (error: FieldsErrorResponse) => void;
  onSettled?: () => void;
};
function useCreateGuestByParticipant({onSuccess, ...props}: UseGuestMutation = {}) {
  const queryClient = useQueryClient();
  const {createGuestByParticipant} = useChekinSDK();
  const {reservationId} = useReservation();

  return useMutation<
    CreateGuestByParticipantResponse,
    FieldsErrorResponse,
    {
      id?: string;
      payload: Omit<CreateGuestByParticipantPayload, 'reservation_id'>;
    }
  >(
    ({payload}) =>
      createGuestByParticipant({reservation_id: reservationId || '', ...payload}),
    {
      onSuccess: async data => {
        await queryClient.refetchQueries([QUERY_CACHE_KEYS.summary]);
        onSuccess?.(data);
        void queryClient.refetchQueries([QUERY_CACHE_KEYS.reservation]);
        void queryClient.refetchQueries([QUERY_CACHE_KEYS.summary]);
        void queryClient.resetQueries([QUERY_CACHE_KEYS.formFields]);
        void queryClient.refetchQueries([QUERY_CACHE_KEYS.guestGroup]);
      },
      ...props,
    },
  );
}

export {useCreateGuestByParticipant};
